import type { CSSProperties, ReactNode } from 'react';
import type { Inscription } from '../../engine';
import { t, type MessageKey } from '../../i18n';
import { useTheme } from '../theme';
import { CardView } from './CardView';
import { PawnFigure } from './PawnFigure';

/** A two-link route for the inscription and Epitaph samples. */
const SAMPLE_ROUTE: Inscription = {
  links: [
    { face: { color: 'R', number: 2 }, wasWild: false, move: { type: 'slide', dx: 0, dy: 2 } },
    { face: { color: 'G', number: 2 }, wasWild: false, move: { type: 'knight', dx: 1, dy: 2 } },
  ],
  recordedScore: 12,
  round: 1,
};

/** Symbols the sea look draws differently, described in its own words. */
const SEA_WORDS = new Set(['south', 'north', 'crowned', 'hlMove', 'hlHop', 'hlEpitaph', 'hlLast']);

function Row({ sample, name }: { sample: ReactNode; name: string }) {
  const key = useTheme() === 'sea' && SEA_WORDS.has(name) ? `sea.${name}` : name;
  return (
    <li className="legend-row">
      <span className="legend-sample" aria-hidden="true">
        {sample}
      </span>
      <span>
        <strong>{t(`legend.${key}.title` as MessageKey)}</strong>{' '}
        {t(`legend.${key}.text` as MessageKey)}
      </span>
    </li>
  );
}

function Pawn(props: Partial<Parameters<typeof PawnFigure>[0]> & { tag?: string }) {
  const { tag, ...rest } = props;
  return (
    <span className="legend-pawn">
      <PawnFigure side="S" crowned={false} immune={false} inscription={null} {...rest} />
      {tag && <span className="pawn-name">{tag}</span>}
    </span>
  );
}

function Square({ hl, dark = false }: { hl?: string; dark?: boolean }) {
  // The capture mark is sized from the board size; a sample square is a sixth of 240px.
  const style = { '--size': '240px' } as CSSProperties;
  return (
    <span className={`legend-sq ${dark ? 'sq-dark' : 'sq-light'} ${hl ?? ''}`} style={style}>
      {hl && hl !== 'sq-last' && <span className="sq-mark" />}
    </span>
  );
}

/** What every symbol on the board, the pawns and the cards means (rules screen and match menu). */
export function SymbolLegend() {
  return (
    <div className="symbol-legend">
      <section aria-labelledby="legend-pawns">
        <h3 id="legend-pawns">{t('legend.pawns')}</h3>
        <ul className="legend-list">
          <Row sample={<Pawn side="S" />} name="south" />
          <Row sample={<Pawn side="N" />} name="north" />
          <Row sample={<Pawn crowned />} name="crowned" />
          <Row sample={<Pawn immune />} name="immune" />
          <Row sample={<Pawn inscription={SAMPLE_ROUTE} />} name="inscription" />
          <Row sample={<Pawn marked />} name="ticket" />
          <Row sample={<Pawn tag="Ada" />} name="named" />
        </ul>
      </section>
      <section aria-labelledby="legend-squares">
        <h3 id="legend-squares">{t('legend.squares')}</h3>
        <ul className="legend-list">
          <Row sample={<Square hl="hl-pawn" />} name="hlPawn" />
          <Row sample={<Square hl="hl-selected" dark />} name="hlSelected" />
          <Row sample={<Square hl="hl-move" />} name="hlMove" />
          <Row sample={<Square hl="hl-hop" dark />} name="hlHop" />
          <Row sample={<Square hl="hl-capture" />} name="hlCapture" />
          <Row sample={<Square hl="hl-collateral" dark />} name="hlCollateral" />
          <Row sample={<Square hl="hl-epitaph" />} name="hlEpitaph" />
          <Row sample={<Square hl="sq-last" dark />} name="hlLast" />
        </ul>
      </section>
      <section aria-labelledby="legend-cards">
        <h3 id="legend-cards">{t('legend.cards')}</h3>
        <ul className="legend-list">
          <Row
            sample={<CardView face={{ color: 'R', number: 2 }} size="sm" label="" />}
            name="card"
          />
          <Row sample={<CardView wild size="sm" label="" />} name="wild" />
          <Row
            sample={<CardView wild face={{ color: 'G', number: 3 }} size="sm" label="" />}
            name="namedWild"
          />
          <Row sample={<CardView epitaph={SAMPLE_ROUTE} size="sm" label="" />} name="epitaph" />
          <Row
            sample={
              <CardView face={{ color: 'B', number: 1 }} size="sm" state="unplayable" label="" />
            }
            name="unplayable"
          />
          <Row sample={<CardView back size="sm" label="" />} name="deck" />
          <Row
            sample={
              <span className="win-pips">
                <span className="pip pip-on" />
                <span className="pip" />
              </span>
            }
            name="wins"
          />
          <Row
            sample={
              <span className="round-dots">
                <span className="round-dot is-done won-S" />
                <span className="round-dot is-current" />
                <span className="round-dot" />
              </span>
            }
            name="rounds"
          />
        </ul>
      </section>
    </div>
  );
}
